/**
 * ReviewQueueModal — HITL approval gate for agent write tools. Drop-in
 * replacement for AgentConfirmationModal (same props, same onSubmit contract:
 * an ordered ResumeDecision[] posted to /disasterchat/resume).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';
import { useAors } from 'hooks/useAors';
import { useIncidentObjectives } from 'components/IAP/hooks/incidentObjectiveHooks';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { INCIDENT } from 'components/IAP/ICSConstants';
import {
  buildDecisionsFromState,
  unitAllowedDecisions,
  useReviewQueue,
} from './useReviewQueue';
import { isAssetImportConfirmation, saveLabel } from './assetImportReviewModel';
import {
  isAutomationRuleConfirmation,
  savedRuleState,
} from './automationRuleReviewModel';
import type { RuleDraftPayload } from 'components/MapPage/CorporateViewTabs/EventsTab/ruleFormModel';
import BatchNavigator from './components/BatchNavigator';
import ChangeReview from './components/ChangeReview';
import type { PendingConfirmation, ResumeDecision } from './types';
import type { SelectOption as ConfigSelectOption } from '../confirmationFieldConfig';

interface ReviewQueueModalProps {
  show: boolean;
  confirmations: PendingConfirmation[];
  askText?: string;
  submitting?: boolean;
  /**
   * Submit the resume decisions. Resolve `false` (or reject) if the resume
   * request failed, so the modal can leave the "applying" spinner and let the
   * reviewer retry instead of hanging on it.
   */
  onSubmit: (decisions: ResumeDecision[]) => void | Promise<boolean | void>;
  onClose?: () => void;
}

const hasText = (s?: string): boolean => !!s && s.trim().length > 0;

const ReviewQueueModal: React.FC<ReviewQueueModalProps> = ({
  show,
  confirmations,
  askText,
  submitting = false,
  onSubmit,
  onClose,
}) => {
  const queue = useReviewQueue(confirmations);
  const {
    state,
    currentUnit,
    currentItem,
    currentState,
    allDecided,
    decidedCount,
  } = queue;

  // Re-seed only when a new non-empty batch arrives. The parent passes a fresh
  // `[]` on every idle render, so re-seeding on the empty case would bump
  // `cycle` every render. `cycle` advances only when one real batch replaces
  // another — a genuine re-review after a submit, never the first batch.
  const [seededFor, setSeededFor] = useState(confirmations);
  if (confirmations.length > 0 && seededFor !== confirmations) {
    const isReReview = seededFor.length > 0;
    setSeededFor(confirmations);
    queue.reset(confirmations, isReReview ? state.cycle + 1 : 1);
  }

  const { data: aors } = useAors();
  const aorOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(aors) ? aors : []).map(
        (a: { id: string; name: string }) => ({
          value: a.id,
          label: a.name,
        })
      ),
    [aors]
  );

  // id→name option lists so linked objective / resources / POC show by name
  // instead of a UUID. Incident-scoped hooks are empty outside an incident.
  const objectiveOptions = useIncidentObjectives(INCIDENT, (objectives) =>
    objectives.map((o) => ({ value: o.objective_id, label: o.name }))
  ).data;
  const { data: resourceSets } = useResourceSets();
  const resourceOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(resourceSets) ? resourceSets : []).map((r) => ({
        value: r.resource_set_id,
        label: r.name,
      })),
    [resourceSets]
  );
  const pocOptions = useIncidentRoster((roster) =>
    roster.map((m) => ({ value: m.userId, label: m.userName ?? m.userId }))
  ).data;

  const optionSets = useMemo<Record<string, ConfigSelectOption[]>>(
    () => ({
      aor: aorOptions,
      objective: objectiveOptions ?? [],
      resource: resourceOptions,
      poc: pocOptions ?? [],
    }),
    [aorOptions, objectiveOptions, resourceOptions, pocOptions]
  );

  const isBatch = state.units.length > 1;
  const respondActive = hasText(currentState?.respondText);
  const hasEdits = !!(
    currentState?.edits && Object.keys(currentState.edits).length > 0
  );
  const primaryLabel = respondActive
    ? 'Send response'
    : currentItem && isAssetImportConfirmation(currentItem)
      ? saveLabel(currentItem, currentState?.edits)
      : hasEdits
        ? 'Accept edits'
        : 'Accept';

  // Honor the allowed_decisions contract per unit: the confirmation's list
  // (empty/missing = allow all), minus reject for required-on-create fields.
  const allowed =
    currentItem && currentUnit
      ? unitAllowedDecisions(currentItem, currentUnit)
      : undefined;
  const allows = (d: 'approve' | 'edit' | 'reject'): boolean =>
    !allowed || allowed.includes(d);
  const canReject = allows('reject');
  // The rule editor's own Save (with its enable prompt) is the accept path.
  const ruleEditor = isAutomationRuleConfirmation(currentItem);
  const primaryAllowed = ruleEditor
    ? false
    : respondActive
      ? canReject
      : hasEdits
        ? allows('edit')
        : allows('approve');

  // Keyboard 1 = primary (accept), 2 = reject. Suppressed while a field has
  // focus so typing doesn't fire a verdict, and gated on allowed_decisions.
  useEffect(() => {
    if (!show || state.phase !== 'review') return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '1' && primaryAllowed) queue.setVerdict('accepted');
      else if (e.key === '2' && !respondActive && canReject)
        queue.setVerdict('rejected');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    state.phase,
    state.index,
    respondActive,
    primaryAllowed,
    canReject,
  ]);

  const title = 'PRATUS AI needs your approval';

  // Escape (or backdrop click) no longer silently does nothing: it asks
  // whether to discard the review. Confirming returns to chat; the pending
  // batch stays on the server so the reviewer can come back to it.
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const requestClose = () => {
    if (state.phase === 'applying') return;
    if (onClose) setDiscardPrompt(true);
  };

  const submitDecisions = async (decisions: ResumeDecision[]) => {
    queue.submit();
    try {
      const ok = await onSubmit(decisions);
      if (ok === false) queue.revertSubmit();
    } catch {
      queue.revertSubmit();
    }
  };

  const handleSubmit = () => submitDecisions(queue.buildDecisions());

  const saveRule = async (payload: RuleDraftPayload) => {
    const itemState = savedRuleState(currentState, payload);
    queue.decideUnit(itemState);
    if (state.units.length === 1) {
      await submitDecisions(
        buildDecisionsFromState(state.items, state.units, {
          ...state.decisions,
          [state.index]: itemState,
        })
      );
    }
  };

  return (
    <Dialog
      open={show}
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className={
          ruleEditor
            ? 'dark tw:flex tw:max-h-[92vh] tw:w-[min(80rem,calc(100vw-2rem))] tw:flex-col tw:gap-3 tw:overflow-hidden tw:p-5 tw:sm:max-w-[80rem]'
            : 'dark tw:flex tw:max-h-[88vh] tw:w-[min(56rem,calc(100vw-2rem))] tw:flex-col tw:gap-3 tw:overflow-hidden tw:p-5 tw:sm:max-w-4xl'
        }
        data-testid="review-queue-modal"
      >
        <DialogHeader className="tw:flex tw:shrink-0 tw:flex-row tw:items-center tw:justify-between tw:gap-3 tw:text-left">
          <div className="tw:min-w-0">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="tw:sr-only">
              Review each proposed change, then accept, edit, or reject it.
            </DialogDescription>
          </div>
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={requestClose}
              aria-label="Close and return to chat menu"
              title="Close — you can return to this review later"
            >
              <XIcon />
            </Button>
          )}
        </DialogHeader>

        {askText && (
          <div className="tw:line-clamp-2 tw:shrink-0 tw:whitespace-pre-wrap tw:rounded-lg tw:border tw:border-border tw:bg-muted/40 tw:px-3 tw:py-2.5 tw:text-[13px] tw:text-foreground">
            {askText}
          </div>
        )}

        {state.phase === 'applying' ? (
          <div className="tw:flex tw:min-h-[180px] tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3.5 tw:text-center tw:text-muted-foreground">
            <Spinner
              className="tw:size-6"
              role="status"
              aria-label="Applying"
            />
            <p className="tw:m-0">
              Writing approved changes &amp; revising your responses…
            </p>
          </div>
        ) : (
          currentItem &&
          currentUnit && (
            <div className="tw:flex tw:min-h-0 tw:flex-1 tw:gap-4 tw:overflow-hidden">
              {isBatch && (
                <BatchNavigator
                  items={state.items}
                  units={state.units}
                  decisions={state.decisions}
                  activeIndex={state.index}
                  decidedCount={decidedCount}
                  onSelect={queue.goto}
                />
              )}
              <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-y-auto">
                <ChangeReview
                  item={currentItem}
                  unit={currentUnit}
                  fieldOrdinal={
                    currentUnit.field !== undefined
                      ? state.units
                          .filter(
                            (u) =>
                              u.confirmationIndex ===
                              currentUnit.confirmationIndex
                          )
                          .indexOf(currentUnit) + 1
                      : undefined
                  }
                  fieldCount={
                    currentUnit.field !== undefined
                      ? state.units.filter(
                          (u) =>
                            u.confirmationIndex ===
                            currentUnit.confirmationIndex
                        ).length
                      : undefined
                  }
                  index={state.index}
                  total={state.units.length}
                  cycle={state.cycle}
                  state={currentState}
                  optionSets={optionSets}
                  canRespond={canReject}
                  onEdit={queue.setEdit}
                  onRespond={queue.setRespondText}
                  onSaveRule={saveRule}
                />
              </div>
            </div>
          )
        )}

        {state.phase === 'review' && (
          <DialogFooter className="tw:flex tw:shrink-0 tw:flex-row tw:flex-nowrap tw:items-center tw:justify-between tw:gap-3 tw:sm:justify-between">
            <div className="tw:flex tw:items-center tw:gap-2.5">
              <Button
                variant="outline"
                onClick={() => queue.setVerdict('rejected')}
                disabled={respondActive || !canReject}
              >
                {ruleEditor ? 'Discard draft' : 'Reject'}
              </Button>
              {!ruleEditor && (
                <Button
                  onClick={() => queue.setVerdict('accepted')}
                  disabled={!primaryAllowed}
                >
                  {primaryLabel}
                </Button>
              )}
            </div>
            <div className="tw:flex tw:items-center tw:gap-2.5">
              <span className="tw:text-[12px] tw:text-muted-foreground">
                {decidedCount} of {state.units.length} decided
              </span>
              <Button
                onClick={handleSubmit}
                disabled={!allDecided || submitting}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>

      <AlertDialog open={discardPrompt} onOpenChange={setDiscardPrompt}>
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard review?</AlertDialogTitle>
            <AlertDialogDescription>
              Your accept/reject choices and edits in this dialog will be lost.
              The agent stays paused, so you can review again later from the
              chat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep reviewing</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setDiscardPrompt(false);
                onClose?.();
              }}
            >
              Discard and close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default ReviewQueueModal;
